/**
 * DailySeoRotator — invisible-but-crawlable component that:
 *   1. Fetches today's rotating SEO bundle from /api/seo/keywords/today
 *   2. Updates <meta name="description"> and <meta name="keywords">
 *   3. Renders a hidden-but-crawlable H2 phrase bank at page bottom so
 *      Google can index all the autocomplete-matched long-tail terms
 *      without cluttering the visible UX.
 *
 * The rotator seed is deterministic per UTC day, so what a Google
 * crawler sees today at 09:00 UTC matches what it sees at 21:00 UTC —
 * critical to avoid cloaking / doorway-page penalties.
 */
import { useEffect, useState } from "react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function DailySeoRotator() {
  const [bundle, setBundle] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/seo/keywords/today`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setBundle(d))
      .catch(() => { /* silent — SEO is progressive */ });
  }, []);

  useEffect(() => {
    if (!bundle) return undefined;

    // Upsert meta description + keywords.
    const upsert = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };
    const tags = [
      upsert('meta[name="description"][data-daily-rotator]',
             { name: "description", content: bundle.meta_description, "data-daily-rotator": "1" }),
      upsert('meta[name="keywords"][data-daily-rotator]',
             { name: "keywords", content: bundle.meta_keywords, "data-daily-rotator": "1" }),
      // Also update the OG description so shared-link previews carry the
      // fresh copy (LinkedIn / Facebook / Slack).
      upsert('meta[property="og:description"][data-daily-rotator]',
             { property: "og:description", content: bundle.meta_description, "data-daily-rotator": "1" }),
    ];

    return () => { for (const t of tags) t.remove(); };
  }, [bundle]);

  if (!bundle) return null;

  return (
    <section
      data-testid="daily-seo-rotator"
      aria-hidden="true"
      className="sr-only"
    >
      <h2>{bundle.primary_keyword} — {bundle.tagline}</h2>
      <p>{bundle.meta_description}</p>
      <ul>
        {bundle.modifiers.map((m, i) => (
          <li key={i}>Central Rentals Canada — {m}</li>
        ))}
      </ul>
    </section>
  );
}
